'use client';

import dynamic from 'next/dynamic';
import { memo, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import { Close } from '@zoomit/icons';
import { getCookie, Z_SESSION_COOKIE_KEY } from '@zoomit/primitives';
import { cssVar, theme } from '@zoomit/zoomit-ds';

import { displayClickApi, displayImpressionApi } from '../../apis/advertising/display';
import { Binoban } from '../../helpers/binoban';
import { onAdClickTag, onAdCloseTag } from '../../helpers/events';
import type { BannerSpec, DisplayType } from '../../types/modules';
import IframeSource from './IframeSource';
import ImageSource from './ImageSource';

const ScriptSource = dynamic(() => import('./ScriptSource'), {
  ssr: false,
});

type DisplayProps = {
  display: DisplayType;
  bannerSpec: BannerSpec;
  zoneId: string;
  zoneCode: string;
  hasCloseButton: boolean;
  hasBorderRadius: boolean;
};

const RenderDisplaySourceComponent = (props: DisplayProps) => {
  const { display, zoneId, zoneCode, hasBorderRadius } = props;
  const source = display.spec.sources[0];

  if (source === undefined) return null;

  const onDisplayClick = async () => {
    Binoban.track('ads_zone_click', {
      zoneId: zoneId,
      displayId: display.id,
    });
    const sessionId = getCookie(Z_SESSION_COOKIE_KEY);
    if (sessionId) {
      await displayClickApi({ displayId: display.id, zoneId: zoneId, sessionId: sessionId });
    }
    onAdClickTag({ bannerId: display.id, bannerName: display.code, zone: zoneCode });
  };

  switch (source.sourceType) {
    case 'Image':
      return (
        <ImageSource
          hasBorderRadius={hasBorderRadius}
          source={source}
          onDisplayClick={onDisplayClick}
          code={`${zoneCode}-${display.code || display.title}`}
        />
      );
    case 'Iframe':
      return (
        <IframeSource
          hasBorderRadius={hasBorderRadius}
          source={source}
          onDisplayClick={onDisplayClick}
          code={`${zoneCode}-${display.code || display.title}`}
        />
      );
    case 'Script':
      return (
        <ScriptSource
          hasBorderRadius={hasBorderRadius}
          source={source}
          onDisplayClick={onDisplayClick}
          code={`${zoneCode}-${display.code || display.title}`}
        />
      );
    default:
      return display.title;
  }
};

const RenderDisplaySource = memo(RenderDisplaySourceComponent);

const Display = (props: DisplayProps) => {
  const {
    display,
    bannerSpec: { maxHeight, maxWidth },
    zoneId,
    zoneCode,
    hasCloseButton,
  } = props;
  const [showDisplay, setShowDisplay] = useState(true);

  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) {
      onImpressionDisplay();
    }
  }, [inView]);

  const source = display.spec.sources[0];
  if (source === undefined) return null;

  const onDisplayClose = () => {
    Binoban.track('ads_zone_close', {
      zoneId: zoneId,
      displayId: display.id,
    });
    setShowDisplay(false);
    onAdCloseTag(zoneCode);
  };

  const onImpressionDisplay = async () => {
    Binoban.track('ads_zone_impression', {
      zoneId: zoneId,
      displayId: display.id,
    });
    await displayImpressionApi({ displayId: display.id, zoneId: zoneId });
  };

  return showDisplay ? (
    <DisplayRoot $maxWidth={maxWidth} $maxHeight={maxHeight} ref={ref}>
      <RenderDisplaySource {...props} />
      {hasCloseButton && (
        <CloseButton onClick={onDisplayClose}>
          <Close fill={cssVar(theme.white)} />
        </CloseButton>
      )}
    </DisplayRoot>
  ) : (
    <></>
  );
};

export default Display;

const DisplayRoot = styled.div<{ $maxWidth: number; $maxHeight: number }>`
  width: 100%;
  max-width: 100%;
  aspect-ratio: ${({ $maxWidth, $maxHeight }) => $maxWidth / $maxHeight};
  position: relative;
  cursor: pointer;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0rem;
  left: 0;
  width: 2rem;
  height: 2rem;
  background-color: var(${theme.black3});
  border: none;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;
